import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ButtonColumn, ConnectedTableComponent, TableColumn, TextColumn } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { ContractLedger } from '@wsphere/warranties/domain';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { RecordSellerPayoutDialog } from '../../dialogs';
import { LedgerStatementDownloadService, LedgerStatementService } from '../../services';

type Contract = ContractLedger.PartyLedgerStatementContract;
type DateRange = { startDate: Date; endDate: Date };
type DateRangeInput = Date | { startDate: Date | null; endDate: Date | null } | null;

const DEFAULT_HISTORY_MONTHS = 3;

// Calendar months, not a fixed day count — "last 3 months" from Sep 23 is Jun 23, not
// whatever Sep 23 minus (3 x an average month length in days) happens to land on.
function defaultDateRange(): DateRange {
  const endDate = new Date();
  const startDate = new Date(endDate);

  // Pin to day 1 before subtracting months: setMonth() overflows into the following month when
  // the current day doesn't exist in the target month (e.g. May 31 minus 3 is "Feb 31", which
  // JS silently rolls forward to Mar 3) — starting from day 1 never overflows, since every month
  // has a 1st. Then clamp back to the original day, or the target month's real last day if it's
  // shorter (May 31 minus 3 months lands on Feb 28, not an overflowed March 3).
  startDate.setDate(1);
  startDate.setMonth(startDate.getMonth() - DEFAULT_HISTORY_MONTHS);
  const lastDayOfTargetMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
  startDate.setDate(Math.min(endDate.getDate(), lastDayOfTargetMonth));

  return { startDate, endDate };
}

// YYYY-MM-DD in the user's local timezone, matching the query param format the backend expects.
function formatDate(date: Date) {
  return date.toLocaleDateString('en-CA');
}

function formatAmount(cents: number, currency: ContractLedger.Currency) {
  // Ledger amounts carry 4 decimal places of a cent (LEDGER_AMOUNT_DECIMAL_PRECISION) — that full
  // precision is what's summed/compared everywhere else on this page (roundToCents, the payout
  // button's threshold checks), this toLocaleString call is the one place it's rounded, purely
  // for display.
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

@Component({
  selector: 'ws-distributor-ledger-statement-page',
  templateUrl: './ledger-statement.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DistributorLedgerStatementPage implements AfterViewInit, OnDestroy {
  private ledgerSvc = inject(LedgerStatementService);
  private securitySvc = inject(SecurityService);
  private downloadSvc = inject(LedgerStatementDownloadService);
  private toastSvc = inject(ToastService);

  @Input() distributorId: string | null = null;
  @PropertyListener('distributorId') private distributorId$ = new BehaviorSubject<string | null>(null);

  /** Needed to gate the "Record Payout" action — the ledger-statement endpoint itself only needs
   * `distributorId` (a query), but recording a payout is a write and checks its own permission
   * against this distributor's scope, mirroring distributor.page.ts's own hasAccess calls. */
  @Input() distributorPermissionKey: string | null = null;

  @ViewChild(ConnectedTableComponent) private table?: ConnectedTableComponent<Contract>;
  @ViewChild(RecordSellerPayoutDialog) private payoutDialog?: RecordSellerPayoutDialog;

  protected error = false;

  protected canRecordPayout$ = from(
    this.securitySvc.hasAccess(
      'wrt_RecordSellerPayout',
      this.distributorPermissionKey ? [this.distributorPermissionKey] : null
    )
  );

  private summaryRefresh$ = new Subject<void>();

  // The per-contract list can be arbitrarily long, so it's fetched by vs-connected-table itself,
  // directly off its own dedicated { items, meta } endpoint — this stream only carries the
  // always-full aggregate (party + totals), used for the summary cards, the clawback banner, and
  // gating each row's payout action against the distributor's overall position.
  statement$ = combineLatest([
    this.distributorId$.pipe(distinctUntilChanged()),
    this.summaryRefresh$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([distributorId]) => {
      this.error = false;
      if (!distributorId) return of(null);

      return this.ledgerSvc.retrieve(distributorId).catch(() => {
        this.error = true;
        return null;
      });
    }),
    // columns$ (below) and the template each subscribe independently — without this, every
    // emission would re-run the whole retrieve() call once per subscriber.
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected contractsEndpoint$ = this.distributorId$.pipe(
    distinctUntilChanged(),
    map(distributorId => (distributorId ? `/api/distributors/${distributorId}/ledger-statement/contracts` : null))
  );

  // Defaults to the last 90 days — the "history view" the contracts table shows unless the user
  // picks a wider range. Always a real bound on both sides (never cleared to unbounded): this is
  // a history view of a specific window, not an optional filter over an otherwise-lifetime list.
  protected filters$ = new BehaviorSubject<DateRange>(defaultDateRange());

  protected contractsQueryParams$ = this.filters$.pipe(
    map(filters => ({ startDate: formatDate(filters.startDate), endDate: formatDate(filters.endDate) }))
  );

  protected exporting = false;

  protected columns$ = combineLatest([
    this.canRecordPayout$,
    this.statement$.pipe(map(statement => statement?.totals ?? [])),
  ]).pipe(map(([canRecordPayout, totals]) => this.buildColumns(canRecordPayout, totals)));

  private resetPageSub?: Subscription;

  // Switching distributors (or the initial load) changes contractsEndpoint$, and changing the
  // date range changes contractsQueryParams$ — vs-connected-table doesn't reset its own page on
  // either (same gap payment-history.page.ts works around the same way), so without this a table
  // viewed past page 1 would stay stranded on a page number the new endpoint/filter may not have.
  ngAfterViewInit() {
    this.resetPageSub = merge(this.contractsEndpoint$, this.filters$).subscribe(() =>
      this.table?.dataSource.setPage(1)
    );
  }

  ngOnDestroy() {
    this.resetPageSub?.unsubscribe();
    this.summaryRefresh$.complete();
    this.filters$.complete();
  }

  // An incomplete or cleared selection is ignored, keeping the last valid range — this is always
  // a bounded history window, never an unbounded "show everything" state (see filters$'s own doc).
  protected onDateRangeChange(dateRange: DateRangeInput) {
    if (dateRange instanceof Date) {
      this.filters$.next({ startDate: dateRange, endDate: dateRange });
    } else if (dateRange?.startDate && dateRange?.endDate) {
      this.filters$.next({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    }
  }

  protected hasOutstandingClawback(statement: ContractLedger.RetrievePartyLedgerStatementQueryResponse) {
    return statement.totals.some(total => this.roundToCents(total.outstandingPayable) < 0);
  }

  /** Whether a balance is still non-zero once rounded to the currency's real two-decimal minor
   * unit — the only unit money actually changes hands in. outstandingPayable is derived from
   * ledger amounts that carry a finer 4-decimal-of-a-cent precision (see
   * LEDGER_AMOUNT_DECIMAL_PRECISION), so a proration remainder can leave a genuine but
   * sub-cent residual that displays as "$0.00" yet still reads as non-zero raw — that residual
   * can never actually be paid out, so it shouldn't keep showing "Record Payout" or the
   * clawback warning. Takes dollars (statement.totals, already converted by the service). */
  protected roundToCents(amount: number) {
    return Math.round(amount * 100) / 100;
  }

  /** Refetches the summary totals and re-runs the table's own current page — after a payout, the
   * row that changed may land on a different page next time since the table is always sorted by
   * outstandingPayable, but there's no reason to jump the user back to page 1 for it. */
  protected refresh() {
    this.summaryRefresh$.next();
    this.table?.dataSource.refresh();
  }

  /** Fetches the branded PDF statement and opens it in a new tab, falling back to a forced
   * download if the popup is blocked — same UX as the warranty declaration's "View Registration". */
  protected async onExport() {
    if (this.exporting) return;

    const distributorId = this.distributorId$.value;
    if (!distributorId) return;

    this.exporting = true;
    try {
      const { startDate, endDate } = this.filters$.value;
      const result = await this.downloadSvc.openStatement({ distributorId, startDate, endDate });

      if (result.status === 'downloaded') {
        this.toastSvc.show({
          type: 'warning',
          title: 'Pop-up Blocked',
          text: 'Please allow pop-ups to view the statement — it has been downloaded instead.',
        });
      }
    } catch (error) {
      this.toastSvc.show({
        type: 'error',
        title: 'Failed to export statement',
        text: error instanceof Error ? error.message : 'Unable to generate the ledger statement. Please try again.',
      });
    } finally {
      this.exporting = false;
    }
  }

  private buildColumns(
    canRecordPayout: boolean,
    totals: ContractLedger.PartyLedgerStatementTotal[]
  ): TableColumn<unknown>[] {
    // Contract # is left as the one stretchy column (variable-length text); every other column
    // is fitContent so it stays shrink-wrapped to its content instead of ballooning to fill
    // leftover width — without it, a right-aligned TextColumn's header ends up visibly detached
    // from its own (left-set) data cells once a neighboring fitContent column (the action
    // button here) collapses to empty and hands its width to this column instead.
    const columns: TableColumn<unknown>[] = [
      new TextColumn<Contract>({ header: 'Contract #' }, item => ({
        text: item.contractNumber,
        classes: 'font-medium',
      })),
      new TextColumn<Contract>({ header: 'Net Allocation', align: 'right', fitContent: true }, item => ({
        text: formatAmount(item.netAllocation, item.currency),
      })),
      new TextColumn<Contract>({ header: 'Net Disbursement', align: 'right', fitContent: true }, item => ({
        text: formatAmount(item.netDisbursement, item.currency),
      })),
      new TextColumn<Contract>({ header: 'Outstanding Payable', align: 'right', fitContent: true }, item => ({
        text: formatAmount(item.outstandingPayable, item.currency),
        classes: Math.round(item.outstandingPayable) < 0 ? 'text-red-600 font-medium' : undefined,
      })),
    ];

    if (canRecordPayout) {
      columns.push(
        new ButtonColumn<Contract>({ header: '', fitContent: true, stickyEnd: true }, item => {
          // Sub-cent noise never counts as a real payable — see roundToCents's own doc.
          if (Math.round(item.outstandingPayable) <= 0) return {};

          const total = totals.find(t => t.currency === item.currency);
          const distributorNetOwesMoney = !total || this.roundToCents(total.outstandingPayable) <= 0;

          if (distributorNetOwesMoney) {
            return {
              text: 'Netted against balance',
              type: 'clear',
              size: 'sm',
              disabled: true,
              tooltip: {
                content:
                  "This distributor's overall balance isn't positive — factor this contract's commission into " +
                  'reconciling their next payment instead of paying it out separately.',
              },
            };
          }

          return {
            text: 'Record Payout',
            type: 'outline',
            size: 'sm',
            click: () => {
              if (!this.payoutDialog) return;
              this.payoutDialog.warrantyId = item.warrantyId;
              this.payoutDialog.contractNumber = item.contractNumber;
              this.payoutDialog.open();
            },
          };
        })
      );
    }

    return columns;
  }
}
